import { Api } from "metabase/api";
import { idTag, invalidateTags, listTag } from "metabase/api/tags";
import type { MetabotMessage } from "metabase/metabot/state/types";
import type {
  Card,
  DeleteSuggestedMetabotPromptRequest,
  ForkMetabotConversationRequest,
  ListMetabotConversationsRequest,
  ListMetabotConversationsResponse,
  MetabotConversationTitleResponse,
  MetabotFeedback,
  MetabotGenerateContentRequest,
  MetabotGenerateContentResponse,
  MetabotId,
  MetabotInfo,
  MetabotSlackSettings,
  MetabotSourceFeedback,
  MetabotStateContext,
  RegenerateSuggestedMetabotPromptsResponse,
  SaveMetabotEntityRequest,
  SuggestedMetabotPromptsRequest,
  SuggestedMetabotPromptsResponse,
  UserMetabotPermissionsResponse,
} from "metabase-types/api";

export type MetabotConversationDetail = {
  conversation_id: string;
  created_at: string;
  title: string | null;
  user_id: number | null;
  forked_from_conversation_id: string | null;
  state?: MetabotStateContext;
  messages: MetabotMessage[];
  context_window_tokens?: number;
};

export const metabotApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    listMetabots: builder.query<{ items: MetabotInfo[] }, void>({
      query: () => ({
        method: "GET",
        url: "/api/metabot/metabot",
      }),
      providesTags: (result) => [
        listTag("metabot"),
        ...(result?.items || []).map((metabot) => idTag("metabot", metabot.id)),
      ],
    }),
    listMetabotConversations: builder.query<
      ListMetabotConversationsResponse,
      ListMetabotConversationsRequest | void
    >({
      query: (params) => ({
        method: "GET",
        url: "/api/metabot/conversations",
        params,
      }),
      providesTags: () => [listTag("metabot-conversations")],
    }),
    getMetabotConversation: builder.query<MetabotConversationDetail, string>({
      query: (conversationId) => ({
        method: "GET",
        url: `/api/metabot/conversations/${conversationId}`,
      }),
    }),
    forkMetabotConversation: builder.mutation<
      MetabotConversationDetail,
      ForkMetabotConversationRequest
    >({
      query: ({ conversation_id, ...body }) => ({
        method: "POST",
        url: `/api/metabot/conversations/${conversation_id}/fork`,
        body,
      }),
    }),
    getMetabotConversationTitle: builder.query<
      MetabotConversationTitleResponse,
      string
    >({
      query: (conversationId) => ({
        method: "GET",
        url: `/api/metabot/conversations/${conversationId}/title`,
      }),
    }),
    updateMetabot: builder.mutation<
      MetabotInfo,
      { id: MetabotId } & Partial<
        Pick<MetabotInfo, "use_verified_content" | "collection_id">
      >
    >({
      query: ({ id, ...updates }) => ({
        method: "PUT",
        url: `/api/metabot/metabot/${id}`,
        body: updates,
      }),
      invalidatesTags: (_, error, { id }) =>
        invalidateTags(error, [
          listTag("metabot"),
          idTag("metabot", id),
          idTag("metabot-prompt-suggestions", id),
        ]),
    }),
    getSuggestedMetabotPrompts: builder.query<
      SuggestedMetabotPromptsResponse,
      SuggestedMetabotPromptsRequest
    >({
      query: ({ metabot_id, ...params }) => ({
        method: "GET",
        url: `/api/metabot/metabot/${metabot_id}/prompt-suggestions`,
        params,
      }),
      providesTags: (_, __, { metabot_id }) => [
        idTag("metabot-prompt-suggestions", metabot_id),
      ],
    }),
    deleteSuggestedMetabotPrompt: builder.mutation<
      void,
      DeleteSuggestedMetabotPromptRequest
    >({
      query: ({ metabot_id, prompt_id }) => ({
        method: "DELETE",
        url: `/api/metabot/metabot/${metabot_id}/prompt-suggestions/${prompt_id}`,
      }),
      invalidatesTags: (_, error, { metabot_id }) =>
        invalidateTags(error, [
          idTag("metabot-prompt-suggestions", metabot_id),
        ]),
    }),
    regenerateSuggestedMetabotPrompts: builder.mutation<
      RegenerateSuggestedMetabotPromptsResponse,
      MetabotId
    >({
      query: (metabot_id) => ({
        method: "POST",
        url: `/api/metabot/metabot/${metabot_id}/prompt-suggestions/regenerate`,
      }),
      invalidatesTags: (_, error, metabot_id) =>
        invalidateTags(error, [
          idTag("metabot-prompt-suggestions", metabot_id),
        ]),
    }),
    metabotGenerateContent: builder.query<
      MetabotGenerateContentResponse,
      MetabotGenerateContentRequest
    >({
      query: (params) => ({
        method: "POST",
        url: "/api/metabot/document/generate-content",
        body: params,
      }),
    }),
    saveMetabotEntity: builder.mutation<Card, SaveMetabotEntityRequest>({
      query: ({ conversation_id, ...body }) => ({
        method: "POST",
        url: `/api/metabot/conversations/${conversation_id}/saved-entity`,
        body,
      }),
      invalidatesTags: (_, error) => invalidateTags(error, [listTag("card")]),
    }),
    submitMetabotFeedback: builder.mutation<void, MetabotFeedback>({
      query: (params) => ({
        method: "POST",
        url: "/api/metabot/feedback",
        body: params,
      }),
    }),
    submitMetabotSourceFeedback: builder.mutation<void, MetabotSourceFeedback>({
      query: (params) => ({
        method: "POST",
        url: "/api/metabot/source-feedback",
        body: params,
      }),
    }),
    updateMetabotSlackSettings: builder.mutation<
      { ok: boolean },
      MetabotSlackSettings
    >({
      query: (settings) => ({
        method: "PUT",
        url: "/api/metabot/slack/settings",
        body: settings,
      }),
      invalidatesTags: ["session-properties"],
    }),
    getUserMetabotPermissions: builder.query<
      UserMetabotPermissionsResponse,
      void
    >({
      query: () => ({
        method: "GET",
        url: "/api/metabot/permissions/user-permissions",
      }),
      providesTags: () => [listTag("metabot-permissions")],
    }),
  }),
});

export const {
  useGetMetabotConversationQuery,
  useForkMetabotConversationMutation,
  useListMetabotConversationsQuery,
  useListMetabotsQuery,
  useUpdateMetabotMutation,
  useGetSuggestedMetabotPromptsQuery,
  useDeleteSuggestedMetabotPromptMutation,
  useRegenerateSuggestedMetabotPromptsMutation,
  useLazyMetabotGenerateContentQuery,
  useSaveMetabotEntityMutation,
  useSubmitMetabotFeedbackMutation,
  useSubmitMetabotSourceFeedbackMutation,
  useUpdateMetabotSlackSettingsMutation,
  useGetUserMetabotPermissionsQuery,
} = metabotApi;
